/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { DeleteResponse } from "../models/DeleteResponse";
import type { LocationAvailabilityResponse } from "../models/LocationAvailabilityResponse";
import type { LocationBooking } from "../models/LocationBooking";
import type { OrganizationMeetingLocationCreate } from "../models/OrganizationMeetingLocationCreate";
import type { OrganizationMeetingLocationAdminResponse } from "../models/OrganizationMeetingLocationAdminResponse";
import type { OrganizationMeetingLocationResponse } from "../models/OrganizationMeetingLocationResponse";
import type { OrganizationMeetingLocationUpdate } from "../models/OrganizationMeetingLocationUpdate";
import type { SessionFrequency } from "../models/SessionFrequency";

import type { CancelablePromise } from "../core/CancelablePromise";
import { OpenAPI } from "../core/OpenAPI";
import { request as __request } from "../core/request";

export class LocationsService {
  /**
   * Get Locations By Org
   * Get all organization meeting locations for a specified organization from the database.
   * @returns OrganizationMeetingLocationResponse Successful Response
   * @throws ApiError
   */
  public static getLocationsByOrg({
    orgId,
    skip,
    limit = 1500,
  }: {
    orgId: number;
    skip?: number;
    limit?: number;
  }): CancelablePromise<
    Array<
      | OrganizationMeetingLocationAdminResponse
      | OrganizationMeetingLocationResponse
    >
  > {
    return __request(OpenAPI, {
      method: "GET",
      url: "/locations/organizations/{org_id}",
      path: {
        org_id: orgId,
      },
      query: {
        skip: skip,
        limit: limit,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Create Location
   * Create a new organization meeting location and
   * returns the resulting location from the database.
   * @returns OrganizationMeetingLocationAdminResponse Successful Response
   * @throws ApiError
   */
  public static createLocation({
    requestBody,
  }: {
    requestBody: OrganizationMeetingLocationCreate;
  }): CancelablePromise<OrganizationMeetingLocationAdminResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/locations",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Update Location
   * Update an organization meeting location by its ID using specified attributes
   * within OrganizationMeetingLocationUpdate to update the database and returns
   * the updated location.
   * @returns OrganizationMeetingLocationAdminResponse Successful Response
   * @throws ApiError
   */
  public static updateLocation({
    locationId,
    requestBody,
  }: {
    locationId: number;
    requestBody: OrganizationMeetingLocationUpdate;
  }): CancelablePromise<OrganizationMeetingLocationAdminResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/locations/{location_id}",
      path: {
        location_id: locationId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Delete Location
   * Delete an organization meeting location that no meeting uses.
   * @returns DeleteResponse Successful Response
   * @throws ApiError
   */
  public static deleteLocation({
    locationId,
  }: {
    locationId: number;
  }): CancelablePromise<DeleteResponse> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/locations/{location_id}",
      path: {
        location_id: locationId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Location Bookings
   * Every accepted meeting at one of the org's locations that overlaps start to
   * until, for the rooms grid. Admins see each meeting's name; a tutor sees the
   * names of their own meetings and every other meeting just as booked.
   * @returns LocationBooking Successful Response
   * @throws ApiError
   */
  public static getLocationBookings({
    orgId,
    start,
    until,
    excludeMeetingId,
  }: {
    orgId: number;
    start: string;
    until: string;
    excludeMeetingId?: number | null;
  }): CancelablePromise<Array<LocationBooking>> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/locations/organizations/{org_id}/bookings",
      path: {
        org_id: orgId,
      },
      query: {
        start: start,
        until: until,
        exclude_meeting_id: excludeMeetingId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Location Availability
   * Determine if an OrganizationMeetingLocation is being used by another Meeting at any point
   * during the provided start and end time. If so, return an object with all the start and end
   * times it is being used for, and other meeting locations that are available during the
   * original start and end time. Only meetings that have been accepted by all tutors are included.
   * With a repeating frequency and ends_on_or_before, busy_dates lists every date of the
   * series (stepped in the org's timezone, as booking does) on which the location is in use.
   * @returns LocationAvailabilityResponse Successful Response
   * @throws ApiError
   */
  public static getLocationAvailability({
    desiredLocationId,
    orgId,
    desiredStart,
    desiredEnd,
    editingMeetingId,
    frequency,
    endsOnOrBefore,
  }: {
    desiredLocationId: number;
    orgId: number;
    desiredStart: string;
    desiredEnd: string;
    editingMeetingId?: number | null;
    frequency?: SessionFrequency | null;
    endsOnOrBefore?: string | null;
  }): CancelablePromise<LocationAvailabilityResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/locations/location/availability{desired_location_id}",
      path: {
        desired_location_id: desiredLocationId,
      },
      query: {
        org_id: orgId,
        desired_start: desiredStart,
        desired_end: desiredEnd,
        editing_meeting_id: editingMeetingId,
        frequency: frequency,
        ends_on_or_before: endsOnOrBefore,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }
}
